import { cn } from '@pesto/utils';
import * as React from 'react';

import { ScrollArea } from './ScrollArea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './Table';

type TableSize = 'sm' | 'md' | 'lg';

type TableVariant = 'default' | 'compact';

export type Column<T> = {
  id: string;
  header: string;
  accessor: (row: T) => React.ReactNode;
  width?: string;
  className?: string;
  headerClassName?: string;
  cellClassName?: string;
  compactLabelClassName?: string;
};

export type SimpleTableProps<T> = {
  data: T[];
  columns: Column<T>[];
  className?: string;
  tableClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string;
  getRowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T, index: number) => void;
  emptyState?: React.ReactNode;
  loading?: boolean;
  loadingState?: React.ReactNode;
  enableHorizontalScroll?: boolean;
  scrollAreaClassName?: string;
  size?: TableSize;
  variant?: TableVariant;
};

const sizeVariants = {
  sm: {
    cellPadding: 'px-2 py-1',
    headerPadding: 'px-2 py-1',
    textSize: 'text-xs',
    headerTextSize: 'text-xs h-auto py-1 whitespace-nowrap text-ellipsis',
    labelTextSize: 'text-xs',
  },
  md: {
    cellPadding: 'px-4 py-2',
    headerPadding: 'px-4 py-3',
    textSize: 'text-sm',
    headerTextSize: 'text-sm',
    labelTextSize: 'text-sm',
  },
  lg: {
    cellPadding: 'px-6 py-3',
    headerPadding: 'px-6 py-4',
    textSize: 'text-base',
    headerTextSize: 'text-base',
    labelTextSize: 'text-base',
  },
} as const;

export function SimpleTable<T>({
  data,
  columns,
  className,
  tableClassName,
  headerClassName,
  bodyClassName,
  rowClassName,
  getRowKey = (_, index) => index,
  onRowClick,
  emptyState = <div className="text-muted-foreground p-4 text-center">No data available</div>,
  loading = false,
  loadingState = <div className="text-muted-foreground p-4 text-center">Loading...</div>,
  enableHorizontalScroll = false,
  scrollAreaClassName,
  size = 'md',
  variant = 'default',
}: SimpleTableProps<T>) {
  const sizes = sizeVariants[size];
  const isCompact = variant === 'compact';

  if (loading) {
    return <div className={cn('w-full', className)}>{loadingState}</div>;
  }

  if (!data.length) {
    return <div className={cn('w-full', className)}>{emptyState}</div>;
  }

  const tableContent = (
    <Table className={tableClassName}>
      {!isCompact && (
        <TableHeader className={headerClassName}>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.id}
                style={{ width: column.width }}
                className={cn(sizes.headerPadding, sizes.headerTextSize, column.headerClassName)}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      )}
      <TableBody className={bodyClassName}>
        {data.map((row, index) => (
          <TableRow
            key={getRowKey(row, index)}
            className={rowClassName}
            onClick={onRowClick ? () => onRowClick(row, index) : undefined}
          >
            {columns.map((column) => (
              <TableCell
                key={`${getRowKey(row, index)}-${column.id}`}
                className={cn(sizes.cellPadding, sizes.textSize, column.className, column.cellClassName)}
              >
                {isCompact ? (
                  <span className={cn(sizes.labelTextSize)}>
                    <span className={cn('text-muted-foreground', column.compactLabelClassName)}>{column.header}: </span>
                    <span className="text-foreground font-medium">{column.accessor(row)}</span>
                  </span>
                ) : (
                  column.accessor(row)
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className={cn('w-full', className)}>
      {enableHorizontalScroll ? (
        <ScrollArea className={cn('w-full', scrollAreaClassName)}>{tableContent}</ScrollArea>
      ) : (
        tableContent
      )}
    </div>
  );
}
